import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store/store'
import { Gig, Application } from '@/types/gig'
// @ts-ignore - Unused import
import { User } from '@/types/auth'
import {
    FiMapPin,
    FiClock,
    FiCalendar,
    FiTag,
    FiUser,
    FiStar,
    FiEye,
    FiBriefcase,
    FiShare2,
    FiEdit2,
    FiArrowLeft,
    FiCheckCircle,
    FiAlertCircle,
    FiRefreshCw
} from 'react-icons/fi'
import { TrashIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Chip from '@/components/ui/Chip'
import CountdownTimer from '@/components/ui/CountdownTimer'
import Avatar from '@/components/ui/Avatar'
import GigImageHero from '@/components/ui/GigImageHero'
import { formatDate, formatTimeAgo, truncateText } from '@/utils/formatters'
import { getCategoryIcon, getCategoryColor } from '@/utils/categoryUtils'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { gigApi } from '@/services/api/gigs'
import { deleteGig } from '@/store/slices/gigsSlice'
import { applicationApi } from '@/services/api/applications'
import { fetchNotifications, fetchUnreadCount } from '@/store/slices/notificationsSlice'
import { ReportButton } from '@/components/reports/ReportButton'
import VerificationBanner from '@/components/verification/VerificationBanner'
import { accessLimitService, AccessLimitStatus } from '@/services/accessLimitService'
import { CurrencyDisplay } from '@/components/currency'
import { useCurrency } from '@/contexts/CurrencyContext'
import { copyToClipboardMobile } from '@/utils/clipboardUtils'

interface GigDetailsPageProps { }

const GigDetailsPage: React.FC<GigDetailsPageProps> = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { showSuccess, showError } = useSnackbarContext()
    const { dialogState, showConfirmDialog, closeDialog, handleConfirm } = useConfirmDialog()

    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)

    const [gig, setGig] = useState<Gig | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [applying, setApplying] = useState(false)
    const [userApplication, setUserApplication] = useState<Application | null>(null)
    const [applicationLimit, setApplicationLimit] = useState<AccessLimitStatus | null>(null)
    const [loadingApplicationLimit, setLoadingApplicationLimit] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)

    // Helper to get category name whether it's string or object
    const getCategoryName = (category: any) => {
        if (!category) return 'Unknown'
        if (typeof category === 'string') {
            return category
        }
        return category.name || category.slug || 'Unknown'
    }

    const getCategoryIconColorClass = (category: string) => {
        const normalized = category.toLowerCase()
        if (normalized.includes('tech')) return 'text-blue-500'
        if (normalized.includes('creative')) return 'text-pink-500'
        if (normalized.includes('market')) return 'text-green-500'
        return 'text-primary-500'
    }

    // Application form state
    const [showApplicationForm, setShowApplicationForm] = useState(false)
    const [applicationData, setApplicationData] = useState({
        message: '',
        proposedRate: '',
        estimatedDuration: '',
        availability: 'immediate'
    })

    useEffect(() => {
        const fetchGigDetails = async () => {
            if (!id) {
                setError('Invalid gig ID')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                // Call real API
                const response = await gigApi.getGigById(id)

                // @ts-ignore - Handle nested response: { success: true, data: {
                if (response.data.data?.gig) {
                    // @ts-ignore - Handle nested response: { success: true, data: {
                    const gigData = response.data.data.gig
                    setGig(gigData)

                    // Check if user has already applied
                    if (isAuthenticated && user && gigData.applications) {
                        const currentUserId = (user as any)._id || (user as any).id
                        // @ts-ignore - app parameter type
                        const userApp = gigData.applications.find(app => {
                            // Support both populated and unpopulated worker references
                            const workerId = typeof app.worker === 'object'
                                ? (app.worker as any)._id || (app.worker as any).id
                                : app.worker
                            return workerId?.toString() === currentUserId?.toString()
                        })
                        setUserApplication(userApp || null)
                    }
                } else {
                    setError('Gig not found')
                }
            } catch (error: any) {
                console.error('Failed to fetch gig:', error)
                setError(error?.response?.data?.error?.message || 'Failed to load gig details')
            } finally {
                setLoading(false)
            }
        }

        fetchGigDetails()
    }, [id, isAuthenticated, user])

    // Increment view count once per session per gig to avoid double-counting
    useEffect(() => {
        if (!id) return
        try {
            const key = `gig_viewed_${id}`
            const alreadyViewed = sessionStorage.getItem(key)
            if (!alreadyViewed) {
                sessionStorage.setItem(key, '1')
                // Fire-and-forget without awaiting (avoid blocking render)
                gigApi.markAsViewed(id).catch(() => { })
            }
        } catch {
            // Ignore storage errors
        }
    }, [id])

    // Check application limits for workers
    useEffect(() => {
        const checkApplicationLimits = async () => {
            if (!user || user.role !== 'worker' || !isAuthenticated) {
                setLoadingApplicationLimit(false)
                return
            }

            try {
                setLoadingApplicationLimit(true)
                const limitStatus = await accessLimitService.canApplyToGig()
                setApplicationLimit(limitStatus)
            } catch (error) {
                console.error('Error checking application limits:', error)
                // Set default values if service fails
                setApplicationLimit({
                    hasAccess: true,
                    isUnlimited: false,
                    requiresUpgrade: false
                })
            } finally {
                setLoadingApplicationLimit(false)
            }
        }

        checkApplicationLimits()
    }, [user, isAuthenticated])

    // Application submission
    const handleApplySubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!gig || !user) return

        // Check access limits before proceeding
        if (!applicationLimit?.hasAccess) {
            showError(applicationLimit?.message || 'You have reached your application limit')
            return
        }

        // Check if user's email is verified
        if (!user.emailVerified) {
            showError('Email verification is required to apply for gigs. Please verify your email address.')
            return
        }

        // Validate message length
        if (applicationData.message.trim().length < 10) {
            showError('Your cover message must be at least 10 characters long')
            return
        }

        setApplying(true)

        try {
            const applicationPayload = {
                gigId: gig._id,
                message: applicationData.message,
                proposedRate: applicationData.proposedRate ? parseFloat(applicationData.proposedRate) : undefined,
                estimatedDuration: applicationData.estimatedDuration || undefined,
                availability: applicationData.availability
            }

            const response = await applicationApi.createApplication(applicationPayload)

            // Handle successful response
            // @ts-ignore - Handle nested response data structure
            if (response.data?.success && response.data?.data?.application) {
                // @ts-ignore - Handle nested response data structure
                const newApplication = response.data.data.application
                setUserApplication(newApplication)
                setShowApplicationForm(false)
                setApplicationData({
                    message: '',
                    proposedRate: '',
                    estimatedDuration: '',
                    availability: 'immediate'
                })

                // Update gig's application count
                setGig(prevGig => prevGig ? {
                    ...prevGig,
                    applicantsCount: (prevGig.applicantsCount || 0) + 1
                } : null)

                showSuccess('Application submitted successfully! The employer will be notified.')

                // Refresh notifications to show the new application notification
                dispatch(fetchUnreadCount())
                dispatch(fetchNotifications({ page: 1, limit: 10 }))
            } else {
                throw new Error('Invalid response format')
            }
        } catch (error: any) {
            console.error('Failed to apply:', error)

            // Check if this is an access limit error
            if (error.accessLimitError?.isAccessLimitError) {
                showError(error.accessLimitError.message || 'You have reached your application limit')
                // Refresh access limit status
                try {
                    const limitStatus = await accessLimitService.canApplyToGig()
                    setApplicationLimit(limitStatus)
                } catch (limitError) {
                    console.error('Failed to refresh application limit:', limitError)
                }
            } else {
                // Extract detailed error messages from API response
                const errorData = error?.response?.data?.error
                let errorMessage = 'Failed to submit application'

                if (errorData) {
                    // Check for detailed validation errors first
                    if (errorData.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
                        // Show all validation errors if multiple, otherwise just the first one
                        if (errorData.details.length > 1) {
                            errorMessage = errorData.details.join('. ')
                        } else {
                            errorMessage = errorData.details[0]
                        }
                    } else if (errorData.message) {
                        // Fall back to generic error message
                        errorMessage = errorData.message
                    }
                } else if (error?.response?.data?.message) {
                    // Alternative error message structure
                    errorMessage = error.response.data.message
                } else if (error?.message) {
                    // Fallback to error message
                    errorMessage = error.message
                }

                showError(errorMessage)
            }
        } finally {
            setApplying(false)
        }
    }

    const handleRefreshGig = async () => {
        if (!id) return

        setIsRefreshing(true)
        try {
            const response = await gigApi.getGigById(id)
            // @ts-ignore - Handle nested response structure
            if (response.data.data?.gig) {
                // @ts-ignore - Handle nested response structure
                setGig(response.data.data.gig)
                showSuccess('Gig data refreshed successfully')
            } else {
                showError('Failed to refresh gig data')
            }

            // Also refresh application limit
            if (user?.role === 'worker') {
                try {
                    const limitStatus = await accessLimitService.canApplyToGig()
                    setApplicationLimit(limitStatus)
                } catch (limitError) {
                    console.error('Failed to refresh application limit:', limitError)
                }
            }
        } catch (error) {
            console.error('Error refreshing gig data:', error)
            showError('Failed to refresh gig data')
        } finally {
            setIsRefreshing(false)
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <LoadingSpinner size="lg" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="max-w-2xl mx-auto text-center py-12">
                <FiAlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Error Loading Gig
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                    Try Again
                </button>
            </div>
        )
    }

    if (!gig) {
        return (
            <div className="max-w-2xl mx-auto text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Gig Not Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    The gig you're looking for doesn't exist or has been removed.
                </p>
                <Link
                    to="/gigs"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                    Browse Gigs
                </Link>
            </div>
        )
    }

    // Handle form input changes
    // @ts-ignore - Unused function
    const handleApplicationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setApplicationData(prev => ({ ...prev, [name]: value }))
    }

    const handleApplicationSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isAuthenticated) {
            showError('Please log in to apply for gigs')
            navigate('/login')
            return
        }

        if (user?.role !== 'worker') {
            showError('Only workers can apply for gigs')
            return
        }

        if (!applicationData.message.trim()) {
            showError('Please provide a cover message')
            return
        }

        // Use the existing handleApplySubmit function
        handleApplySubmit(e)
    }

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: gig?.title,
                    text: gig?.description,
                    url: window.location.href,
                })
                showSuccess('Gig shared successfully!')
            } catch (error: any) {
                // Ignore user cancellations (AbortError)
                if (error.name !== 'AbortError') {
                    console.error('Error sharing:', error)
                    showError('Failed to share gig')
                }
            }
        } else {
            // Fallback: use mobile-compatible clipboard utility
            const copied = await copyToClipboardMobile(window.location.href)
            if (copied) {
                showSuccess('Link copied to clipboard!')
            } else {
                showError('Failed to copy link to clipboard')
            }
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    if (error || !gig) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center">
                    <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Gig Not Found
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                        {error || 'The gig you\'re looking for doesn\'t exist or has been removed.'}
                    </p>
                    <Link
                        to="/gigs"
                        className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <FiArrowLeft className="mr-2" />
                        Back to Gigs
                    </Link>
                </div>
            </div>
        )
    }

    const employer = typeof gig.employer === 'object' ? gig.employer : null
    const isOwner = user && employer && user._id === employer._id
    const canApply = user?.role === 'worker' && gig.status === 'active' && !isOwner && !userApplication && user?.emailVerified

    return (
        <>
            <Helmet>
                <title>{gig.title} - SideGO</title>
                <meta name="description" content={truncateText(gig.description, 160)} />
                <meta property="og:title" content={gig.title} />
                <meta property="og:description" content={truncateText(gig.description, 160)} />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button and Refresh */}
                <div className="mb-6 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        <FiArrowLeft className="mr-2" />
                        Back to gigs
                    </button>
                    <button
                        onClick={handleRefreshGig}
                        disabled={isRefreshing}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Refresh gig data"
                    >
                        <FiRefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        <span className="ml-2 hidden sm:inline">Refresh</span>
                    </button>
                </div>

                {/* Hero Image Section */}
                <div className="mb-6 md:mb-8">
                    <GigImageHero
                        images={gig.images}
                        alt={gig.title}
                        className="h-40 sm:h-44 md:h-80 lg:h-96"
                        aspectRatio="banner"
                        overlay={true}
                        autoSlide={gig.images && gig.images.length > 1}
                        slideInterval={6000}
                    >
                        {/* Content overlay on hero image - responsive */}
                        <div className="absolute inset-0 flex flex-col justify-between p-2 sm:p-3 md:p-4">
                            {/* Action buttons - positioned at top right */}
                            <div className="flex items-start justify-end">
                                <div className="flex items-center gap-1 sm:gap-2">
                                    <button
                                        onClick={handleShare}
                                        className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white rounded-lg transition-colors text-xs sm:text-sm"
                                    >
                                        <FiShare2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Share</span>
                                    </button>

                                    {/* Edit button for gig owner */}
                                    {isOwner && (
                                        <Link
                                            to={`/dashboard/gigs/${gig._id}/edit`}
                                            className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600/80 hover:bg-primary-700 backdrop-blur-sm text-white rounded-lg transition-colors text-xs sm:text-sm"
                                        >
                                            <FiEdit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                            <span className="hidden sm:inline">Edit</span>
                                        </Link>
                                    )}
                                </div>
                            </div>

                            {/* Category, urgency, and status chips - positioned at bottom left */}
                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                {/* Category and urgency */}
                                <div className="flex items-center gap-1 sm:gap-2 bg-black/50 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg">
                                    <span className={`text-base sm:text-lg ${getCategoryIconColorClass(getCategoryName(gig.category))}`}>
                                        {getCategoryIcon(getCategoryName(gig.category))}
                                    </span>
                                    <span className="text-white text-xs sm:text-sm font-medium whitespace-nowrap">
                                        {gig.urgency ? gig.urgency.toUpperCase() : 'MEDIUM'}
                                    </span>
                                </div>

                                {/* Status chip */}
                                <Chip
                                    variant={gig.status === 'active' ? 'success' : gig.status === 'completed' ? 'warning' : 'error'}
                                    size="sm"
                                    className="shadow-lg text-xs sm:text-sm"
                                >
                                    {gig.status.charAt(0).toUpperCase() + gig.status.slice(1)}
                                </Chip>

                                {/* Featured chip */}
                                {gig.featured && (
                                    <Chip variant="warning" size="sm" icon={<span>⭐</span>} className="shadow-lg text-xs sm:text-sm">
                                        Featured
                                    </Chip>
                                )}
                            </div>
                        </div>
                    </GigImageHero>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 mb-6">
                            {/* Gig Header */}
                            <div className="mb-6">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                    {gig.title}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    <div className="flex items-center gap-1">
                                        <FiMapPin className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">{gig.location.location}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <FiCalendar className="w-4 h-4 flex-shrink-0" />
                                        <span className="whitespace-nowrap">Posted {formatTimeAgo(gig.createdAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <FiEye className="w-4 h-4 flex-shrink-0" />
                                        <span className="whitespace-nowrap">{(gig as any).viewsCount ?? (gig as any).viewCount ?? 0} views</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <FiBriefcase className="w-4 h-4 flex-shrink-0" />
                                        <span className="whitespace-nowrap">{(gig as any).applicantsCount ?? (gig as any).applicationCount ?? (Array.isArray(gig.applications) ? gig.applications.length : 0)} applications</span>
                                    </div>
                                </div>

                                {/* Tags */}
                                {gig.tags && gig.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {gig.tags.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="px-2.5 sm:px-3 py-1.5 sm:py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs sm:text-sm font-medium rounded-full"
                                            >
                                                <FiTag className="inline w-3 h-3 mr-1" />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="flex items-center gap-2">
                                    {isOwner && (
                                        <button
                                            onClick={() => {
                                                showConfirmDialog(
                                                    async () => {
                                                        try {
                                                            // dispatch delete action
                                                            // @ts-ignore
                                                            await dispatch(deleteGig(gig._id) as any)
                                                            showSuccess('Gig deleted')
                                                            navigate('/gigs')
                                                        } catch (err: any) {
                                                            console.error('Failed to delete gig', err)
                                                            showError(err?.response?.data?.error?.message || 'Failed to delete gig')
                                                        }
                                                    },
                                                    {
                                                        title: 'Delete Gig',
                                                        message: `Are you sure you want to delete "${gig.title}"? This action cannot be undone.`,
                                                        confirmText: 'Delete',
                                                        cancelText: 'Cancel',
                                                        type: 'danger'
                                                    }
                                                )
                                            }}
                                            className="inline-flex items-center gap-2 px-3 py-1.5 text-red-600 hover:text-red-700 border border-red-300 hover:border-red-400 rounded-lg transition-colors text-sm"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                            Delete Gig
                                        </button>
                                    )}

                                    <ReportButton
                                        reportedResource={{
                                            type: 'gig',
                                            resourceId: gig._id
                                        }}
                                        variant="icon"
                                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                    />
                                </div>
                            </div>





                            {/* Description */}
                            <div className="prose dark:prose-invert max-w-none mb-8">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Description
                                </h3>
                                <div className="whitespace-pre-wrap text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {gig.description}
                                </div>
                            </div>

                            {/* Requirements */}
                            {gig.requirements.length > 0 && (
                                <div className="mb-8">
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                        Requirements
                                    </h3>
                                    <ul className="space-y-2 sm:space-y-3">
                                        {gig.requirements.map((requirement, index) => (
                                            <li key={index} className="flex items-start gap-2">
                                                <FiCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                                                    {requirement}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        {/* Payment & Duration Info */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 mb-6">
                            <div className="space-y-4 sm:space-y-5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-400 font-semibold text-base sm:text-lg">$</span>
                                        <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                            Budget
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                                            <CurrencyDisplay
                                                amount={gig.payment.amount}
                                                currency={gig.payment.currency}
                                                variant="default"
                                            />
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 capitalize">
                                            {gig.payment.type}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FiClock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                        <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                            Duration
                                        </span>
                                    </div>
                                    <span className="text-sm sm:text-base text-gray-900 dark:text-white font-medium">
                                        {gig.duration.estimated}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FiTag className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                        <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                            Category
                                        </span>
                                    </div>
                                    <span className="text-sm sm:text-base text-gray-900 dark:text-white font-medium capitalize">
                                        {getCategoryName(gig.category)}
                                    </span>
                                </div>
                            </div>

                            {/* Application Status or CTA */}
                            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                {userApplication ? (
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <FiCheckCircle className="w-5 h-5 text-green-500" />
                                            <span className="font-medium text-green-700 dark:text-green-300">
                                                Application Submitted
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Status: <span className="capitalize font-medium">{userApplication.status}</span>
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                            Applied {formatTimeAgo(userApplication.appliedAt)}
                                        </p>

                                        {/* Countdown Timer for Accepted Applications */}
                                        {userApplication.status === 'accepted' && gig.duration && (
                                            <div className="mt-4">
                                                <CountdownTimer
                                                    startTime={userApplication.appliedAt}
                                                    duration={gig.duration.estimated}
                                                    endDate={gig.duration.endDate}
                                                    className="max-w-sm mx-auto"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : !gig.requireSubmission ? (
                                    <div className="text-center text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <FiMapPin className="inline-block mr-2 w-5 h-5" />
                                        <div className="font-medium">On-site Work Opportunity</div>
                                        <div className="text-sm mt-1">
                                            This is an on-site job. Work will be completed in person and verified directly with the client.
                                            {canApply && (
                                                <div className="mt-2">
                                                    <button
                                                        onClick={() => setShowApplicationForm(true)}
                                                        disabled={Boolean(applicationLimit && !applicationLimit.hasAccess)}
                                                        className={`w-full font-medium py-2 px-3 rounded-lg transition-colors text-sm ${applicationLimit && !applicationLimit.hasAccess
                                                            ? 'bg-red-500 text-white cursor-not-allowed'
                                                            : 'bg-primary-600 hover:bg-primary-700 text-white'
                                                            }`}
                                                    >
                                                        {applicationLimit && !applicationLimit.hasAccess
                                                            ? user?.emailVerified
                                                                ? 'Upgrade Plan to Apply'
                                                                : 'Verification Required to Apply'
                                                            : applicationLimit && !applicationLimit.isUnlimited
                                                                ? `Express Interest (${applicationLimit.remaining} left)`
                                                                : 'Express Interest'
                                                        }
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : canApply ? (
                                    <div className="space-y-4">
                                        {/* Application Access Limit Banner */}
                                        {applicationLimit && !applicationLimit.hasAccess && (
                                            <VerificationBanner
                                                user={user!}
                                                accessLimit={applicationLimit}
                                                type="application"
                                                variant="compact"
                                            />
                                        )}

                                        {/* Show compact banner for users with access but limits (when remaining <= 10) */}
                                        {applicationLimit && applicationLimit.hasAccess && !applicationLimit.isUnlimited && applicationLimit.remaining !== undefined && applicationLimit.remaining <= 10 && (
                                            <VerificationBanner
                                                user={user!}
                                                accessLimit={applicationLimit}
                                                type="application"
                                                variant="compact"
                                            />
                                        )}

                                        <button
                                            onClick={() => setShowApplicationForm(true)}
                                            disabled={Boolean(applicationLimit && !applicationLimit.hasAccess)}
                                            className={`w-full font-medium py-3 px-4 rounded-lg transition-colors ${applicationLimit && !applicationLimit.hasAccess
                                                ? 'bg-red-500 text-white cursor-not-allowed'
                                                : 'bg-primary-600 hover:bg-primary-700 text-white'
                                                }`}
                                        >
                                            {applicationLimit && !applicationLimit.hasAccess
                                                ? user?.emailVerified
                                                    ? 'Upgrade Plan to Apply'
                                                    : 'Verification Required to Apply'
                                                : applicationLimit && !applicationLimit.isUnlimited && applicationLimit.remaining !== undefined && applicationLimit.remaining <= 10
                                                    ? `Apply for this Gig (${applicationLimit.remaining} left)`
                                                    : 'Apply for this Gig'
                                            }
                                        </button>
                                    </div>
                                ) : gig.status === 'filled' ? (
                                    <div className="text-center text-gray-500 dark:text-gray-400">
                                        This gig has been filled
                                    </div>
                                ) : !isAuthenticated ? (
                                    <Link
                                        to="/login"
                                        className="block w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors text-center"
                                    >
                                        Login to Apply
                                    </Link>
                                ) : user?.role === 'worker' && !user?.emailVerified ? (
                                    <div className="text-center text-amber-600 dark:text-amber-400 text-sm bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                                        <FiAlertCircle className="inline-block mr-2" />
                                        Please verify your email address to apply for gigs
                                    </div>
                                ) : user?.role === 'employer' ? (
                                    <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
                                        Switch to a worker account to apply for gigs
                                    </div>
                                ) : isOwner ? (
                                    <Link
                                        to={`/dashboard/gigs/${gig._id}/edit`}
                                        className="block w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors text-center"
                                    >
                                        Edit Gig
                                    </Link>
                                ) : null}
                            </div>
                        </div>

                        {/* Employer Info */}
                        {employer && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    About the Client
                                </h3>

                                <div className="flex items-start gap-3 sm:gap-4">
                                    <Avatar
                                        avatarId={employer.profile?.avatar}
                                        alt={`${employer.profile.firstName} ${employer.profile.lastName}`}
                                        size="md"
                                        className="bg-primary-100 dark:bg-primary-900 flex-shrink-0"
                                        fallbackIcon={<FiUser className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />}
                                    />

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                            <h4 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                                                {employer.profile?.firstName} {employer.profile?.lastName}
                                            </h4>
                                            {employer.profile?.verified && (
                                                <FiCheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" title="Verified" />
                                            )}
                                        </div>

                                        {employer.profile?.company && (
                                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 truncate">
                                                {employer.profile.company}
                                            </p>
                                        )}

                                        <div className="flex items-center gap-1.5 mb-2">
                                            <div className="flex items-center">
                                                {[...Array(5)].map((_, i) => (
                                                    <FiStar
                                                        key={i}
                                                        className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${i < Math.floor(employer.ratings?.average || 0)
                                                            ? 'text-yellow-500 fill-current'
                                                            : 'text-gray-300 dark:text-gray-600'
                                                            }`}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                                {employer.ratings?.average?.toFixed(1)} ({employer.ratings?.count} reviews)
                                            </span>
                                        </div>

                                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                            Member since {formatDate(employer.createdAt)}
                                        </p>

                                        {employer.profile?.bio && (
                                            <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mt-3 line-clamp-2">
                                                {truncateText(employer.profile.bio, 120)}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <Link
                                        to={`/profile/${employer._id}`}
                                        className="text-xs sm:text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                                    >
                                        View full profile →
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Application Modal */}
                {showApplicationForm && canApply && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        Apply for Gig
                                    </h3>
                                    <button
                                        onClick={() => setShowApplicationForm(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Application Access Limit & Verification Banner */}
                                {applicationLimit && !applicationLimit.hasAccess && (
                                    <VerificationBanner
                                        user={user!}
                                        accessLimit={applicationLimit}
                                        type="application"
                                        variant="inline"
                                        className="mb-4"
                                    />
                                )}

                                {/* Show compact banner for users with access but limits (when remaining <= 10) */}
                                {applicationLimit && applicationLimit.hasAccess && !applicationLimit.isUnlimited && applicationLimit.remaining !== undefined && applicationLimit.remaining <= 10 && (
                                    <VerificationBanner
                                        user={user!}
                                        accessLimit={applicationLimit}
                                        type="application"
                                        variant="compact"
                                        className="mb-4"
                                    />
                                )}

                                <form onSubmit={handleApplicationSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Cover Message *
                                        </label>
                                        <textarea
                                            value={applicationData.message}
                                            onChange={(e) => setApplicationData(prev => ({ ...prev, message: e.target.value }))}
                                            required
                                            minLength={10}
                                            rows={4}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                                applicationData.message.trim().length > 0 && applicationData.message.trim().length < 10
                                                    ? 'border-red-500 dark:border-red-500'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                            placeholder="Explain why you're the right person for this gig... (minimum 10 characters)"
                                        />
                                        <div className="flex justify-between items-center mt-1">
                                            <p className={`text-xs ${
                                                applicationData.message.trim().length < 10
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : 'text-gray-500 dark:text-gray-400'
                                            }`}>
                                                {applicationData.message.trim().length < 10
                                                    ? `${10 - applicationData.message.trim().length} more characters needed`
                                                    : 'Minimum length met ✓'
                                                }
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {applicationData.message.length} characters
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Proposed Rate (Optional)
                                        </label>
                                        <input
                                            type="number"
                                            value={applicationData.proposedRate}
                                            onChange={(e) => setApplicationData(prev => ({ ...prev, proposedRate: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Your rate in GHS"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="gig-application-availability" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Availability
                                        </label>
                                        <select
                                            id="gig-application-availability"
                                            value={applicationData.availability}
                                            onChange={(e) => setApplicationData(prev => ({ ...prev, availability: e.target.value }))}
                                            aria-label="Application availability"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        >
                                            <option value="immediate">Immediately</option>
                                            <option value="within_week">Within a week</option>
                                            <option value="within_month">Within a month</option>
                                            <option value="flexible">Flexible</option>
                                        </select>
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setShowApplicationForm(false)}
                                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={applying || Boolean(applicationLimit && !applicationLimit.hasAccess)}
                                            className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${applying || (applicationLimit && !applicationLimit.hasAccess)
                                                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                : 'bg-primary-600 text-white hover:bg-primary-700'
                                                }`}
                                        >
                                            {applying
                                                ? 'Submitting...'
                                                : applicationLimit && !applicationLimit.hasAccess
                                                    ? 'Verification Required'
                                                    : applicationLimit && !applicationLimit.isUnlimited && applicationLimit.remaining !== undefined && applicationLimit.remaining <= 10
                                                        ? `Apply (${applicationLimit.remaining} left)`
                                                        : 'Submit Application'
                                            }
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={dialogState.isOpen}
                title={dialogState.title}
                message={dialogState.message}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                type={dialogState.type}
                loading={dialogState.loading}
                onConfirm={handleConfirm}
                onClose={closeDialog}
            />
        </>
    )
}

export default GigDetailsPage